/**
 * Import helper functions for JSON files
 * Adapted from CirculatorWebpage importHelpers.ts
 */

import type { FixedRouteStop, FixedRoute, FixedRoutesExportData } from '../../types/fixedRoutes';

/**
 * Basic validation for imported data structure
 */
function isValidExportData(data: any): data is FixedRoutesExportData {
  return (
    data &&
    typeof data === 'object' &&
    Array.isArray(data.stops) &&
    Array.isArray(data.routes) &&
    typeof data.trafficMultiplier === 'number' &&
    data.trafficMultiplier > 0 &&
    typeof data.exportDate === 'string'
  );
}

/**
 * Validate a FixedRouteStop
 */
function isValidStop(stop: any): stop is FixedRouteStop {
  return (
    stop &&
    typeof stop.id === 'string' &&
    stop.id.length > 0 &&
    typeof stop.name === 'string' &&
    stop.name.length > 0 &&
    stop.coordinates &&
    typeof stop.coordinates.lat === 'number' &&
    typeof stop.coordinates.lng === 'number'
  );
}

/**
 * Validate a FixedRouteSegment
 */
function isValidSegment(segment: any): boolean {
  return (
    segment &&
    typeof segment.fromStopId === 'string' &&
    typeof segment.toStopId === 'string' &&
    typeof segment.travelTimeMinutes === 'number' &&
    segment.travelTimeMinutes > 0
  );
}

/**
 * Validate a FixedRouteStopConfig
 */
function isValidStopConfig(config: any): boolean {
  return (
    config &&
    typeof config.stopId === 'string' &&
    typeof config.waitTimeMinutes === 'number' &&
    config.waitTimeMinutes >= 0
  );
}

/**
 * Validate a FixedRoute
 */
function isValidRoute(route: any): route is FixedRoute {
  if (
    !route ||
    typeof route.id !== 'string' ||
    route.id.length === 0 ||
    typeof route.name !== 'string' ||
    route.name.length === 0 ||
    typeof route.startTime !== 'string' ||
    !/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(route.startTime) ||
    !Array.isArray(route.stops) ||
    route.stops.length < 2 ||
    !Array.isArray(route.segments)
  ) {
    return false;
  }

  // Validate stops
  if (!route.stops.every(isValidStopConfig)) {
    return false;
  }

  // Validate segments
  if (!route.segments.every(isValidSegment)) {
    return false;
  }

  // Validate segment count matches stop count
  if (route.segments.length !== route.stops.length - 1) {
    return false;
  }

  // Validate segment connectivity
  for (let i = 0; i < route.segments.length; i++) {
    const segment = route.segments[i];
    const expectedFrom = route.stops[i]?.stopId;
    const expectedTo = route.stops[i + 1]?.stopId;
    if (segment.fromStopId !== expectedFrom || segment.toStopId !== expectedTo) {
      return false;
    }
  }

  return true;
}

/**
 * Import JSON file
 */
export async function importFromJSON(file: File): Promise<FixedRoutesExportData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const data = JSON.parse(text);
        
        if (!isValidExportData(data)) {
          throw new Error('Invalid data format: missing required fields');
        }

        // Validate stops
        const validStops = data.stops.filter(isValidStop);
        if (validStops.length !== data.stops.length) {
          throw new Error('Some stops are invalid');
        }

        // Validate routes
        const validRoutes = data.routes.filter(isValidRoute);
        if (validRoutes.length !== data.routes.length) {
          throw new Error('Some routes are invalid');
        }

        resolve({
          stops: validStops,
          routes: validRoutes,
          trafficMultiplier: data.trafficMultiplier,
          exportDate: data.exportDate,
        });
      } catch (error) {
        reject(new Error(`Invalid JSON file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file);
  });
}

/**
 * Validate imported data
 */
export function validateImportedData(data: unknown): data is FixedRoutesExportData {
  try {
    return isValidExportData(data);
  } catch {
    return false;
  }
}

/**
 * Validate and extract stops from imported data
 */
export function extractStops(data: FixedRoutesExportData): FixedRouteStop[] {
  return data.stops.filter(isValidStop);
}

/**
 * Validate and extract routes from imported data
 */
export function extractRoutes(data: FixedRoutesExportData): FixedRoute[] {
  return data.routes.filter(isValidRoute);
}

